import { useContext, useEffect, useLayoutEffect, useMemo, useState, ReactNode } from "react"
import { AuthContext } from "./user-context/user-context"
import axios from "axios";
import { useSelectedBranch } from "./selected-branch-context";
import { API_ENDPOINT } from "../configurations/global.config";

interface AxiosInterceptorProviderProps {
    children: ReactNode;
}

/**
 * Provider that sets up axios interceptors on the default axios instance.
 * This ensures all axios calls throughout the app automatically get:
 * - Auth headers from AuthContext
 * - context_branch_id query param when applicable (only for Employee/Organisation users)
 * 
 * No need to change existing axios imports - they all automatically get the middleware!
 */
export const AxiosInterceptorProvider = ({ children }: AxiosInterceptorProviderProps) => {
    const {config} = useContext(AuthContext);
    const {selectedBranch, isLoading, canSelectBranch} = useSelectedBranch();

    // Track interceptor ID for cleanup
    const [interceptorId, setInterceptorId] = useState<number | null>(null);
    
    // Stable storage for promise resolvers and latest values (created once, persists across renders)
    const branchResolversStorage = useMemo(() => {
        const resolvers = new Set<() => void>();
        return {
            resolvers,
            resolveAll: () => {
                resolvers.forEach(resolve => resolve());
                resolvers.clear();
            },
            // Store latest values so interceptors can access current state even after waiting
            latestValues: {
                selectedBranch: null as typeof selectedBranch,
                isLoading: false,
                canSelectBranch: false,
            }
        };
    }, []);

    // Update latest values in storage and resolve waiting promises when branch is ready
    useEffect(() => {
        // Update latest values
        branchResolversStorage.latestValues.selectedBranch = selectedBranch;
        branchResolversStorage.latestValues.isLoading = isLoading;
        branchResolversStorage.latestValues.canSelectBranch = canSelectBranch;

        // Resolve waiting promises when branch is ready
        // We wait until either:
        // 1. User can't select branch (no need to wait)
        // 2. Loading is complete (branch is either set or determined to be unavailable)
        // 3. Branch is now available (selectedBranch is set)
        const shouldResolve = !canSelectBranch || !isLoading || (canSelectBranch && selectedBranch !== null);
        if (shouldResolve && branchResolversStorage.resolvers.size > 0) {
            branchResolversStorage.resolveAll();
        }
    }, [isLoading, canSelectBranch, selectedBranch, branchResolversStorage]);

    // Set up and update interceptor when dependencies change
    // Use useLayoutEffect to ensure interceptor is ready synchronously before any API calls
    useLayoutEffect(() => {
        // Remove old interceptor if it exists
        if (interceptorId !== null) {
            (axios as any).interceptors.request.eject(interceptorId);
        }

        // Create new interceptor with current values in closure
        const requestInterceptor = async (axiosConfig: any) => {
            // Merge auth headers from config
            if (config?.headers) {
                const headers = config.headers;
                Object.keys(headers).forEach((key) => {
                    const value = headers[key as keyof typeof headers];
                    if (value) {
                        axiosConfig.headers.set(key, value);
                    }
                });
            }

            // Only process requests to our API endpoint
            const urlString = axiosConfig.url || '';
            const isApiRequest = 
                !!urlString && !urlString.includes("/branches/") &&
                ((API_ENDPOINT && urlString.includes(API_ENDPOINT)) ||
                 urlString.includes('/api/') ||
                 urlString.includes('/v1/'));

            if (isApiRequest) {
                // Get current values from storage (always up-to-date)
                const currentCanSelectBranch = branchResolversStorage.latestValues.canSelectBranch;
                const currentIsLoading = branchResolversStorage.latestValues.isLoading;
                const currentSelectedBranch = branchResolversStorage.latestValues.selectedBranch;

                // Only enforce branch context for Employee/Organisation users (canSelectBranch)
                // If user can select branch, wait for branch to be ready
                // Wait if: canSelectBranch is true AND (still loading OR branch not set yet)
                if (currentCanSelectBranch && (currentIsLoading || currentSelectedBranch === null)) {
                    // Wait for branch to be ready
                    await new Promise<void>((resolve) => {
                        branchResolversStorage.resolvers.add(resolve);
                    });
                }

                // After waiting, get the latest values again (they may have changed)
                const latestSelectedBranch = branchResolversStorage.latestValues.selectedBranch;
                const latestCanSelectBranch = branchResolversStorage.latestValues.canSelectBranch;

                // Only add context_branch_id for Employee/Organisation users (canSelectBranch)
                // Check if we should add context_branch_id
                if (latestCanSelectBranch && latestSelectedBranch?.id) {
                    // Check if context_branch_id is already present
                    const hasInParams = axiosConfig.params && 'context_branch_id' in axiosConfig.params;
                    const hasInUrl = urlString.includes('context_branch_id=');
                    
                    if (!hasInParams && !hasInUrl) {
                        // Add context_branch_id to query params
                        if (!axiosConfig.params) {
                            axiosConfig.params = {};
                        }
                        axiosConfig.params.context_branch_id = latestSelectedBranch.id;
                    }
                }
            }

            return axiosConfig;
        };

        // Register new interceptor on the default axios instance
        const newInterceptorId = (axios as any).interceptors.request.use(requestInterceptor);
        setInterceptorId(newInterceptorId);

        // Cleanup on unmount
        return () => {
            if (newInterceptorId !== null) {
                (axios as any).interceptors.request.eject(newInterceptorId);
            }
        };
    }, [config, canSelectBranch, isLoading, selectedBranch, branchResolversStorage]);

    return <>{children}</>;
}

